import { useState, useEffect, useRef } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { fetchWithAdminAuth, getAdminAuthToken } from '../utils/adminAuth';

interface Performer {
  id: string;
  name: string;
  type: string[];
  pricePerMin: number;
  state: string;
  image?: string | null;
  description?: string;
}

interface FormData {
  type: string[];
  name: string;
  image: string;
  pricePerMin: string;
  state: string;
  description: string;
}

const typeOptions: { value: string; label: string }[] = [
  { value: 'amor', label: 'Amor' },
  { value: 'relacoes', label: 'Relações' },
  { value: 'reconciliacao', label: 'Reconciliação' },
  { value: 'carreira', label: 'Carreira' },
  { value: 'negocios', label: 'Negócios' },
  { value: 'financas', label: 'Finanças' },
  { value: 'espiritualidade', label: 'Espiritualidade' },
  { value: 'proposito', label: 'Propósito' },
  { value: 'autoconhecimento', label: 'Autoconhecimento' },
  { value: 'decisoes', label: 'Decisões' },
  { value: 'planeamento', label: 'Planeamento' },
  { value: 'estrategia-pessoal', label: 'Estratégia Pessoal' },
];

const typeLabelMap: Record<string, string> = Object.fromEntries(
  typeOptions.map(o => [o.value, o.label])
);

const emptyFormData: FormData = {
  type: [],
  name: '',
  image: '',
  pricePerMin: '',
  state: 'active',
  description: ''
};

export default function Consultores() {
  const [performers, setPerformers] = useState<Performer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<FormData>({ ...emptyFormData });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Track whether we are editing or creating
  const [editingPerformer, setEditingPerformer] = useState<Performer | null>(null);
  const isEditing = editingPerformer !== null;

  useEffect(() => {
    fetchPerformers();
  }, []);

  const fetchPerformers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetchWithAdminAuth('/api/performers');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch performers: ${response.statusText}`);
      }
      
      const data = await response.json();
      const performersList = Array.isArray(data) ? data : (data?.performers ?? []);
      setPerformers(performersList);
    } catch (err) {
      console.error('Error fetching performers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load performers');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (selectedType: string) => {
    setFormData(prev => ({
      ...prev,
      type: prev.type.includes(selectedType)
        ? prev.type.filter(t => t !== selectedType)
        : [...prev.type, selectedType]
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setModalError('Apenas imagens JPEG, PNG, WebP e GIF são permitidas');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setModalError('A imagem não pode exceder 5MB');
      return;
    }

    setImageFile(file);
    setModalError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImagePreview(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null);
    setSuccess(false);

    // Validation
    if (!formData.name.trim()) {
      setModalError('Nome é obrigatório');
      return;
    }
    if (!formData.pricePerMin || parseFloat(formData.pricePerMin) <= 0) {
      setModalError('Preço por minuto deve ser maior que 0');
      return;
    }
    if (formData.type.length === 0) {
      setModalError('Selecione pelo menos uma especialidade');
      return;
    }

    try {
      setIsSubmitting(true);

      // Upload image first if a file was selected
      let imageUrl = formData.image;
      if (imageFile) {
        setIsUploading(true);
        const uploadData = new FormData();
        uploadData.append('image', imageFile);

        const token = getAdminAuthToken();
        const uploadResponse = await fetch('/api/performers/upload-image', {
          method: 'POST',
          headers: {
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: uploadData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          throw new Error(errorData.error || 'Erro ao fazer upload da imagem');
        }

        const uploadResult = await uploadResponse.json();
        imageUrl = uploadResult.imageUrl;
        setIsUploading(false);
      }

      const payload = {
        type: formData.type,
        name: formData.name,
        image: imageUrl,
        pricePerMin: parseFloat(formData.pricePerMin),
        state: formData.state,
        description: formData.description
      };

      let response: Response;

      if (isEditing) {
        // PATCH to update
        response = await fetchWithAdminAuth(`/api/performers/${editingPerformer.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
      } else {
        // PUT to create
        response = await fetchWithAdminAuth('/api/performers', {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || `Erro ao ${isEditing ? 'atualizar' : 'criar'} consultor: ${response.statusText}`);
      }

      setSuccess(true);
      // Reset form
      resetForm();

      // Close modal after success and refresh data
      setTimeout(() => {
        setIsModalOpen(false);
        fetchPerformers();
      }, 1500);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : `Erro ao ${isEditing ? 'atualizar' : 'criar'} consultor`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...emptyFormData });
    setImageFile(null);
    setImagePreview(null);
    setEditingPerformer(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpenCreate = () => {
    resetForm();
    setModalError(null);
    setSuccess(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (performer: Performer) => {
    setEditingPerformer(performer);
    const validTypeValues = typeOptions.map(o => o.value);
    setFormData({
      type: (performer.type || []).filter(t => validTypeValues.includes(t)),
      name: performer.name,
      image: performer.image || '',
      pricePerMin: String(performer.pricePerMin ?? ''),
      state: performer.state,
      description: performer.description || ''
    });
    setImageFile(null);
    setImagePreview(performer.image || null);
    setModalError(null);
    setSuccess(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSubmitting) {
      setIsModalOpen(false);
      setModalError(null);
      setSuccess(false);
      resetForm();
    }
  };

  const getStateColor = (state: string) => {
    const normalized = state.toLowerCase();
    
    if (normalized === 'active') {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
          Ativo
        </span>
      );
    }
    
    if (normalized === 'inactive') {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">
          Inativo
        </span>
      );
    }

    if (normalized === 'paused') {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
          Pausado
        </span>
      );
    }
    
    return <span className="text-gray-400">{state}</span>;
  };

  if (loading) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-5">
        <Card
          title="Consultores"
          subtitle="Gestão de consultores humanos (perfil, horários, preços, comissões)."
          action={<Button variant="primary" onClick={handleOpenCreate}>+ Novo consultor</Button>}
        >
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-3 text-gray-400">A carregar consultores...</span>
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-3 sm:px-6 py-4 sm:py-5">
        <Card
          title="Consultores"
          subtitle="Gestão de consultores humanos (perfil, horários, preços, comissões)."
          action={<Button variant="primary" onClick={handleOpenCreate}>+ Novo consultor</Button>}
        >
          <div className="text-center py-12 text-red-400">
            <p>Erro ao carregar consultores: {error}</p>
            <button
              onClick={fetchPerformers}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
            >
              Tentar novamente
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      <Card
        title="Consultores"
        subtitle="Gestão de consultores humanos (perfil, horários, preços, comissões)."
        action={<Button variant="primary" onClick={handleOpenCreate}>+ Novo consultor</Button>}
      >
        {/* Table */}
        <div className="rounded-xl border border-gray-800 bg-gray-900 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Imagem</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Especialidades</TableHead>
                <TableHead>Preço / min</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {performers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    Nenhum consultor encontrado
                  </TableCell>
                </TableRow>
              ) : (
                performers.map((performer) => (
                  <TableRow key={performer.id}>
                    <TableCell>
                      {performer.image ? (
                        <img
                          src={performer.image}
                          alt={performer.name}
                          className="w-12 h-12 rounded-lg object-cover border border-gray-700"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg border border-gray-700 bg-gray-800 flex items-center justify-center text-xs text-gray-400">
                          {performer.name
                            .split(' ')
                            .map((part) => part[0])
                            .slice(0, 2)
                            .join('')
                            .toUpperCase()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-white">{performer.name}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {performer.type.map(t => (
                          <span key={t} className="inline-block px-2 py-1 text-xs bg-purple-500/20 text-purple-300 rounded">
                            {typeLabelMap[t] || t}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>€ {performer.pricePerMin}</TableCell>
                    <TableCell>{getStateColor(performer.state)}</TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => handleOpenEdit(performer)}
                        className="inline-flex px-3 py-1.5 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 transition"
                      >
                        Editar
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Modal (Create / Edit) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto border border-gray-800">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {isEditing ? 'Editar Consultor' : 'Novo Consultor'}
              </h2>
              <button
                onClick={handleCloseModal}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-white disabled:opacity-50 transition"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Error Message */}
              {modalError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
                  {modalError}
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-green-400">
                  {isEditing ? 'Consultor atualizado com sucesso!' : 'Consultor criado com sucesso!'}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nome <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Ex: Inês Silva"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Imagem do Consultor
                </label>
                <div className="space-y-3">
                  <label className="flex items-center justify-center w-full px-4 py-3 bg-gray-800 border-2 border-dashed border-gray-600 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-gray-750 transition-colors">
                    <div className="flex flex-col items-center gap-1">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm text-gray-400">
                        {imageFile ? imageFile.name : 'Clique para selecionar imagem'}
                      </span>
                      <span className="text-xs text-gray-500">JPEG, PNG, WebP ou GIF (máx. 5MB)</span>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleImageChange}
                      className="hidden"
                      disabled={isSubmitting}
                    />
                  </label>
                  {(imagePreview || formData.image) && (
                    <div className="flex items-center gap-3">
                      <img
                        src={imagePreview || formData.image}
                        alt="Pré-visualização"
                        className="w-20 h-20 rounded-lg object-cover border border-gray-700"
                      />
                      {imageFile && (
                        <button
                          type="button"
                          onClick={() => {
                            setImageFile(null);
                            setImagePreview(isEditing && editingPerformer?.image ? editingPerformer.image : null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-sm text-red-400 hover:text-red-300 transition"
                        >
                          Remover
                        </button>
                      )}
                    </div>
                  )}
                  {isUploading && (
                    <div className="flex items-center gap-2 text-sm text-purple-400">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                      A fazer upload...
                    </div>
                  )}
                </div>
              </div>

              {/* Price Per Minute */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Preço por Minuto (€) <span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  name="pricePerMin"
                  value={formData.pricePerMin}
                  onChange={handleInputChange}
                  placeholder="1.20"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  disabled={isSubmitting}
                />
              </div>

              {/* Type (Specialties) */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Especialidades <span className="text-red-400">*</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {typeOptions.map(opt => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.type.includes(opt.value)}
                        onChange={() => handleTypeChange(opt.value)}
                        disabled={isSubmitting}
                        className="w-4 h-4 rounded border-gray-600 text-purple-600 focus:ring-purple-600 focus:ring-offset-gray-800 cursor-pointer"
                      />
                      <span className="text-gray-300">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* State */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Estado <span className="text-red-400">*</span>
                </label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  disabled={isSubmitting}
                >
                  <option value="active">Ativo</option>
                  <option value="inactive">Inativo</option>
                  <option value="paused">Pausado</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Descrição
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Digite uma descrição sobre o consultor..."
                  rows={4}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent resize-none"
                  disabled={isSubmitting}
                />
              </div>

              {/* Modal Footer */}
              <div className="flex gap-3 pt-6 border-t border-gray-800">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 disabled:opacity-50 transition font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition font-medium flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      A guardar...
                    </>
                  ) : (
                    isEditing ? 'Guardar Alterações' : 'Criar Consultor'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
